//@ts-check
import dayjs from 'dayjs';

/**
 * Get the business date based on a start hour threshold
 * Times before startHour are considered part of the previous day
 * @param {string | number | Date | dayjs.Dayjs} date - Date to convert
 * @param {number} startHour - Hour threshold (0-23)
 * @returns {dayjs.Dayjs | null}
 */
const getBusinessDate = (date, startHour) => {
  if (date == null) return null;

  // Handle YYYYMMDD format (8 digits)
  if (String(date).length === 8) {
    return dayjs(date, 'YYYYMMDD');
  }

  const dateObj = dayjs(date);
  return dateObj.hour() < startHour ? dateObj.subtract(1, 'day') : dateObj;
};

// Alias for backward compatibility
export const getDateD = getBusinessDate;

/**
 * Format time with optional next-day prefix
 * @param {string | number | Date | dayjs.Dayjs} time
 * @param {number} startHour
 * @param {string} format - dayjs format string
 * @returns {string}
 */
const formatTimeWithPrefix = (time, startHour, format) => {
  if (time == null || time === -1 || time === '' || time === 0) {
    return '不明';
  }

  const timeObj = dayjs(time);
  const formatted = timeObj.format(format);

  return timeObj.hour() < startHour ? `翌${formatted}` : formatted;
};

/**
 * Format time with business date context
 * @param {string | number | Date | dayjs.Dayjs} time
 * @param {number} startHour
 * @param {string} format - dayjs format string
 * @returns {string}
 */
const formatWithBusinessDate = (time, startHour, format) => {
  const businessDate = getBusinessDate(time, startHour);
  if (!businessDate) return '不明';
  const actualTime = dayjs(time);

  // Check if actual date is next day relative to business date
  const isNextDay = actualTime.date() !== businessDate.date() ||
    actualTime.month() !== businessDate.month() ||
    actualTime.year() !== businessDate.year();

  const prefix = isNextDay ? '翌' : '';
  return businessDate.format(format.replace('HH:mm', `${prefix}HH:mm`));
};


export const getDate = (date, startHour) => {
  if (String(date).length === 8) {
    return String(date);
  }
  if (String(date).length === 13) {
    let d = new Date(date);
    const hour = d.getHours();
    if (hour < startHour) {
      d.setDate(d.getDate() - 1);
    }
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();
    return `${year}${month.toString().padStart(2, '0')}${day.toString().padStart(2, '0')}`;
  }
  return getBusinessDate(date, startHour)?.format('YYYYMMDD');
};

export const getDateNum = (date, startHour) => {
  return Number(getDate(date, startHour));
};

export const timeToHm = (time, startHour) => {
  if (time == null || time == -1 || time == '' || time == 0) {
    return -1;
  }
  const dTime = dayjs(time);
  if (startHour <= dTime.hour()) {
    return dTime.hour() * 60 + dTime.minute();
  }
  return (dTime.hour() + 24) * 60 + dTime.minute();
};

const _hmToStr = (hour, minute) => {
  if (hour < 24) {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  }
  return `翌${(hour - 24).toString()}:${minute.toString().padStart(2, '0')}`;
};

const _hmToStrNaive = (hour, minute) => {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

export const hmToTime = (hm, basic) => {
  if (hm == -1 || hm == null || hm == '') {
    return '';
  }
  if (hm == -2) {
    return hmToTime(basic.btStart);
  }
  if (hm == -3) {
    return hmToTime(basic.btEnd);
  }
  const minute = hm % 60;
  const hour = (hm - minute) / 60;
  return _hmToStr(hour, minute);
};
export const hmToHours = (hm, basic) => {
  if (hm == -1 || hm == null || hm == '') {
    return '';
  }
  if (hm == -2) {
    return hmToTime(basic.btStart);
  }
  if (hm == -3) {
    return hmToTime(basic.btEnd);
  }
  const minute = hm % 60;
  const hour = (hm - minute) / 60;
  return _hmToStrNaive(hour, minute);
};

export const hourToReadableStr = (hour) => {
  if (hour == null || isNaN(hour)) {
    return '不明';
  }
  const readableHour = hour >= 24 ? '翌' + String(hour - 24) : String(hour);
  return readableHour + '時';
};

// #region time formatters


export const timeToHmStr = (time, startHour) =>
  formatTimeWithPrefix(time, startHour, 'HH:mm');

export const timeToHmsStr = (time, startHour) =>
  formatTimeWithPrefix(time, startHour, 'HH:mm:ss');

export const timeToYmdStr = (time, startHour) =>
  getBusinessDate(time, startHour)?.format('YYYY/MM/DD(dd)') ?? '不明';

export const timeToMdHmStr = (time, startHour) =>
  formatWithBusinessDate(time, startHour, 'MM/DD(dd) HH:mm');

export const timeToYmdHmStr = (time, startHour) =>
  formatWithBusinessDate(time, startHour, 'YYYY/MM/DD(dd) HH:mm');

export const timeToYmdHmsStr = (time, startHour) =>
  formatWithBusinessDate(time, startHour, 'YYYY/MM/DD(dd) HH:mm:ss');
export const timeToHmStr2 = (time, date) => {
  const dateObject = dayjs(date);
  const timeObject = dayjs(time);
  const timeStr = timeObject.format('HH:mm');
  if (dateObject.isSame(timeObject, 'day')) {
    return timeStr;
  } else if (dateObject.isAfter(timeObject, 'day')) {
    return `翌${timeStr}`;
  } else {
    return `前${timeStr}`;
  }
};

// #endregion


export const hmToTimeUnix = (date, hm, basic) => {
  if (hm == -1 || hm == null || hm == '') {
    return 0;
  }
  if (hm == -2) {
    return hmToTimeUnix(date, basic.btStart);
  }
  if (hm == -3) {
    return hmToTimeUnix(date, basic.btEnd);
  }
  const minute = hm % 60;
  const hour = (hm - minute) / 60;
  return dayjs(date).startOf('day').add(hour, 'hour').add(minute, 'minute').valueOf();
};

export const toTime = (hm) => {
  const minute = hm % 60;
  const hour = (hm - minute) / 60;
  return _hmToStr(hour, minute);
};


const dayArray = [
  '(日)', '(月)', '(火)', '(水)', '(木)', '(金)', '(土)',
];

export const toDateStr = (num) => {
  const year = Math.floor(num / 10000);
  const month = Math.floor((num - (year * 10000)) / 100);
  const day = num - year * 10000 - month * 100;
  const d = new Date(year, month - 1, day);
  const dayOfWeek = dayArray[dayjs(d).day()];
  return month + '月' + day + '日' + dayOfWeek;
};

export const dateToDayjs = (num) => {
  const year = Math.floor(num / 10000);
  const month = Math.floor((num - (year * 10000)) / 100);
  const day = num - year * 10000 - month * 100;
  const d = new Date(year, month - 1, day);
  return dayjs(d);
};

/**
 * 
 * @param {number} year 
 * @param {number} month 1-12
 * @param {number} add 
 * @returns 
 */
export const addMonth = (year, month, add) => {
  const d = new Date(year, month - 1, 1);
  const date = dayjs(d);
  const added = date.add(add, 'month');
  return added.format('YYYYMM');
};

/**
 * 
 * @param {number} year 
 * @param {number} month 1-12
 * @returns 
 */
export const getDaysOfMonth = (year, month) => {
  if (!year || !month)
    return 31;
  if (month == 2) {
    if (
      year % 172800 == 0 ||
      (year % 400 == 0 && year % 3200 != 0) ||
      (year % 4 == 0 && year % 100 != 0)
    )
      return 29;
    return 28;
  }
  return [31, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month] ?? 31;
};

export const getWeeklyAllDates = (year, month, btStartHour) => {
  const now = getBusinessDate(dayjs(), btStartHour);
  const dates = [];

  // month が０・null・undefined の場合、月が選択されていないので１年全部を使う
  let startDay = month ? now.year(year).month(month - 1).date(1) : now.year(year).month(0).date(1);
  const dayOfMonthStart = startDay.day(); // 0 = sunday, 1 = monday, ... 6 = saturday
  const prevMonthDays = (6 + dayOfMonthStart) % 7; // 前の月の最後の月曜日までの日の数

  let endDay = startDay.date(startDay.daysInMonth());
  if (!month) {
    endDay = now.year(year).month(11);
    endDay = endDay.date(endDay.daysInMonth());
  }
  const dayOfMonthEnd = endDay.day();
  const nextMonthDays = (7 - dayOfMonthEnd) % 7; // 次の月の最初の日曜日までの日の数

  const numDays = endDay.diff(startDay, 'days') + 1;

  for (let i = prevMonthDays; i > 0; i--) {
    const day = startDay.subtract(i, 'day');
    dates.push(day.format('YYYYMMDD'));
  }
  for (let i = 0; i < numDays; i++) {
    const day = startDay.add(i, 'day');
    dates.push(day.format('YYYYMMDD'));
  }
  for (let i = 1; i <= nextMonthDays; i++) {
    const day = endDay.add(i, 'day');
    dates.push(day.format('YYYYMMDD'));
  }
  return dates;
};